import type { ComponentType, FC } from 'react';

import { ConfirmDeleteApiKeyModal } from '@features/api-keys/components/modals/ConfirmDeleteApiKeyModal';
import { CreateApiKeyModal } from '@features/api-keys/components/modals/CreateApiKeyModal';
import { ForgotPasswordModal } from '@features/authentication/components/modals/ForgotPasswordModal';
import { SelectTenantModal } from '@features/authentication/components/modals/SelectTenantModal';
import { UserAgreementModal } from '@features/authentication/components/modals/UserAgreementModal';
import { CreateAgentModalController } from '@features/integrations/components/modals/create-agent/CreateAgentModalController';
import { AddNewInvestigationModal } from '@features/investigations/components/modals/add-new-investigation/AddNewInvestigationModal';
import { InvestigationReportModal } from '@features/investigations/components/modals/investigation-report/InvestigationReport.modal';
import { CreateNewUserModal } from '@features/user-management/components/modals/CreateNewUserModal';
import { DeleteUserModal } from '@features/user-management/components/modals/DeleteUserModal';
import { FilteringModal } from '@shared/components/findings/faceted-filtering/FilteringModal';
import { MODAL_KEYS, type ModalKeyType } from '@shared/components/ui/custom/global-modal-manager/global-manager.types';

/* NOTE: We cast to 'any' here because TypeScript cannot statically verify
     that the generic 'modalProps' object matches the specific props required
     by the dynamically selected component.
  */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MODAL_COMPONENTS: Record<string, ComponentType<any>> = {
  [MODAL_KEYS.SELECT_TENANT]: SelectTenantModal,
  [MODAL_KEYS.FORGOT_PASSWORD]: ForgotPasswordModal,
  [MODAL_KEYS.USER_AGREEMENT]: UserAgreementModal,
  [MODAL_KEYS.INVESTIGATION_REPORT]: InvestigationReportModal,
  [MODAL_KEYS.FILTERING_MODAL]: FilteringModal,
  [MODAL_KEYS.ADD_NEW_INVESTIGATION]: AddNewInvestigationModal,
  [MODAL_KEYS.ADD_NEW_USER]: CreateNewUserModal,
  [MODAL_KEYS.DELETE_USER]: DeleteUserModal,
  [MODAL_KEYS.CREATE_API_KEY]: CreateApiKeyModal,
  [MODAL_KEYS.DELETE_API_KEY]: ConfirmDeleteApiKeyModal,
  [MODAL_KEYS.CREATE_AGENT]: CreateAgentModalController,
};

interface GlobalModalManagerProps {
  activeModal: ModalKeyType | null;
  closeModal: (key: ModalKeyType) => void;
  props: Record<string, unknown>;
}

export const GlobalModalManager: FC<GlobalModalManagerProps> = ({ activeModal, closeModal, props }) => {
  if (!activeModal) return null;

  const ModalComponent = MODAL_COMPONENTS[activeModal];

  if (!ModalComponent) {
    console.warn(`No modal found for key: ${activeModal}`);
    return null;
  }

  const handleClose = () => {
    closeModal(activeModal);
  };

  return <ModalComponent {...props} onClose={handleClose} open={true} />;
};
